import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

// Signature masked line-by-line reveal — own IntersectionObserver + CSS transition
// (framer-motion's whileInView proved unreliable with percent transforms here)
export function MaskedLines({ lines, delay = 0, className = "", lineClassName = "", immediate = false }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (immediate) {
      const t = requestAnimationFrame(() => setShow(true));
      return () => cancelAnimationFrame(t);
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "-60px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [immediate]);
  const items = lines.map((l) => (typeof l === "string" ? { text: l } : l));
  return (
    <span ref={ref} className={`block ${className}`}>
      {items.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
          <span
            className={`masked-line block will-change-transform ${show ? "masked-line-in" : ""} ${line.className || ""} ${lineClassName}`}
            style={{ transitionDelay: `${(delay + i * 0.13) * 1000}ms` }}
          >
            {line.text}
          </span>
        </span>
      ))}
    </span>
  );
}

export function Reveal({ children, delay = 0, y = 28, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

// Numbered manifesto chapter heading
export function Chapter({ num, overline, title, dark = false, className = "" }) {
  return (
    <div className={className}>
      <Reveal>
        <p className={`flex items-center gap-3 text-xs sm:text-sm tracking-[0.25em] uppercase font-semibold ${dark ? "text-sand/70" : "text-primary"}`} data-testid={`chapter-${num}`}>
          <span className="font-serif italic text-lg normal-case tracking-normal">{num}</span>
          <span className={`h-px w-10 ${dark ? "bg-sand/40" : "bg-primary/40"}`} />
          {overline}
        </p>
      </Reveal>
      <h2 className={`mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02] ${dark ? "text-sand" : "text-ink"}`}>
        <MaskedLines lines={Array.isArray(title) ? title : [title]} />
      </h2>
    </div>
  );
}

// Parallax image for heroes — settles on load, drifts on scroll
export function ParallaxImg({ src, alt, className = "", speed = 0.18 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", `${speed * 100}%`]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div initial={{ scale: 1.18, opacity: 0.6 }} animate={{ scale: 1.05, opacity: 1 }} transition={{ duration: 1.8, ease: EASE }} className="h-full w-full">
        <motion.img src={src} alt={alt} style={{ y }} className="h-full w-full scale-110 object-cover will-change-transform" />
      </motion.div>
    </div>
  );
}
